import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Grid,
  Paper,
  Stack,
  Typography,
  Avatar,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Drawer,
  useMediaQuery,
  useTheme,
  Tooltip,
} from '@mui/material';
import { Link, useLocation, useNavigate } from 'react-router-dom';

// Icons
import MenuIcon from '@mui/icons-material/Menu';
import DashboardIcon from '@mui/icons-material/Dashboard';
import SimCardIcon from '@mui/icons-material/SimCard';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag';
import EventNoteIcon from '@mui/icons-material/EventNote';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import OfflineBoltIcon from '@mui/icons-material/OfflineBolt';
import ArticleRoundedIcon from '@mui/icons-material/ArticleRounded';
import PermContactCalendarIcon from '@mui/icons-material/PermContactCalendar';
import ViewQuiltIcon from '@mui/icons-material/ViewQuilt';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import PublishedWithChangesIcon from '@mui/icons-material/PublishedWithChanges';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';

import { getCookie } from '../../utils/helper';

const SIDEBAR_WIDTH = 280;

function Dashboard() {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [mobileOpen, setMobileOpen] = useState(false);
  const [authDomain, setAuthDomain] = useState({});
  const hostname = window.location.hostname;

  useEffect(() => {
    const token = getCookie('token');
    setAuthDomain(token);
    document.title = 'Tổng quan quản trị website ' + hostname;
  }, [hostname]);

  // Định nghĩa danh sách Menu dựa trên Logic code cũ của bạn
  const fullMenu = [
    {
      title: 'Bảng Sim Cá Nhân',
      icon: <SimCardIcon />,
      path: '/ctv',
      color: '#EC4899',
      show: true, // Luôn hiện theo code cũ
    },
    {
      title: 'Tổng Quan',
      icon: <DashboardIcon />,
      path: '/dashboard',
      color: '#3B82F6',
      show: true,
    },
    {
      title: 'Quản Lý Admin',
      icon: <PermContactCalendarIcon />,
      path: '/dashboard/admin',
      color: '#6366F1',
      show: ['1', '2', '3', '6', '7', '4'].includes(authDomain?.permission),
    },
    {
      title: 'Excel Sim',
      icon: <EventNoteIcon />,
      path: '/dashboard/excel',
      color: '#10B981',
      show: authDomain?.permission === '3' && hostname === 'khoso.com.vn',
    },
    {
      title: 'Lọc Excel',
      icon: <EventNoteIcon />,
      path: '/dashboard/loc',
      color: '#10B981',
      show: authDomain?.permission === '3' && hostname === 'sale.khoso.com.vn',
    },
    {
      title: 'Quản Lý Giao Diện',
      icon: <ViewQuiltIcon />,
      path: '/dashboard/giaodien',
      color: '#8B5CF6',
      show: ['1', '2', '3', '6', '7', '4'].includes(authDomain?.permission),
    },

    {
      title: 'Công Tác Viên',
      icon: <PeopleAltIcon />,
      path: '/dashboard/dsctv',
      color: '#F59E0B',
      show: ['3', '7'].includes(authDomain?.permission),
    },
    {
      title: 'SIM Đang Load',
      icon: <PublishedWithChangesIcon />,
      path: '/dashboard/dsloadsim',
      color: '#14B8A6',
      show: ['3', '7', '5'].includes(authDomain?.permission),
    },
    {
      title: 'Quản Lý Sản Phẩm',
      icon: <ShoppingBagIcon />,
      path: '/dashboard/shops',
      color: '#EF4444',
      show: ['3', '6'].includes(authDomain?.permission),
    },
    {
      title: 'Làm Nhiều Ảnh',
      icon: <AddPhotoAlternateIcon />,
      path: '/photo',
      color: '#06B6D4',
      show: ['1', '2', '3', '6', '7', '4'].includes(authDomain?.permission),
    },
    {
      title: 'Công Nợ (Thu-Chi)',
      icon: <EventNoteIcon />,
      path: '/congno',
      color: '#64748B',
      show: authDomain?.permission !== '5',
    },
    {
      title: 'Thông Báo Sim',
      icon: <NotificationsActiveIcon />,
      path: '/dashboard/notifisim',
      color: '#F43F5E',
      show: ['1', '2', '3', '6', '7', '4'].includes(authDomain?.permission),
    },
    {
      title: 'Flash Sale',
      icon: <OfflineBoltIcon />,
      path: '/dashboard/flashsale',
      color: '#F97316',
      show: ['3', '6', '7', '4'].includes(authDomain?.permission),
    },
    {
      title: 'Bài Viết News',
      icon: <ArticleRoundedIcon />,
      path: '/dashboard/news',
      color: '#22C55E',
      show: ['1', '2', '3', '6', '7', '4'].includes(authDomain?.permission),
    },
    {
      title: 'Sim DiGiShop',
      icon: <CardGiftcardIcon />,
      path: '/dashboard/digishop',
      color: '#D946EF',
      show: authDomain?.permission === '3',
    },
  ];

  return (
    <Container sx={{ pb: '90px', pl: '5px', pr: '5px' }}>
      <Box sx={{ bgcolor: '#F8FAFC' }}>
        {/* MAIN PANEL CONTENT */}
        <Box>
          <Box sx={{ mb: 4, mt: 1 }}>
            <Typography variant="h4" sx={{ fontWeight: 800, color: '#1E293B', fontFamily: 'FS' }}>
              Tổng Quan Quản Trị
            </Typography>
            <Typography variant="body1" sx={{ color: '#64748B', fontFamily: 'UTM' }}>
              Chào mừng bạn đến với hệ thống quản lý {hostname}
            </Typography>
          </Box>

          {/* GRID CARDS DISPLAY */}
          <Grid container spacing={2}>
            {fullMenu.map(
              (item, index) =>
                item.show &&
                item.path !== '/dashboard' &&
                item.path !== '/dashboard/admin' && (
                  <Grid item xs={12} sm={6} lg={4} xl={3} key={index}>
                    <Paper
                      component={Link}
                      to={item.path}
                      elevation={0}
                      sx={{
                        p: 2,
                        display: 'flex',
                        alignItems: 'center',
                        textDecoration: 'none',
                        borderRadius: '16px',
                        bgcolor: '#FFF',
                        border: '1px solid #E2E8F0',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        '&:hover': {
                          transform: 'translateY(-5px)',
                          boxShadow: '0 12px 24px -10px rgba(0,0,0,0.1)',
                          borderColor: item.color,
                        },
                      }}
                    >
                      <Avatar
                        sx={{
                          width: 52,
                          height: 52,
                          borderRadius: '12px',
                          bgcolor: `${item.color}15`,
                          color: item.color,
                          mr: 2,
                        }}
                      >
                        {item.icon}
                      </Avatar>
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography
                          sx={{
                            color: '#64748B',
                            fontSize: '0.7rem',
                            fontWeight: 800,
                            textTransform: 'uppercase',
                            mb: 0.5,
                            fontFamily: 'UTM',
                          }}
                        >
                          Quản lý
                        </Typography>
                        <Typography
                          sx={{
                            color: '#1E293B',
                            fontWeight: 900,
                            fontSize: '0.95rem',
                            lineHeight: 1.2,
                            fontFamily: 'UTM-Avo',
                          }}
                        >
                          {item.title}
                        </Typography>
                      </Box>
                      <Tooltip title="Truy cập">
                        <IconButton size="small" sx={{ bgcolor: '#F8FAFC' }}>
                          <TrendingUpIcon sx={{ color: '#10B981', fontSize: 18 }} />
                        </IconButton>
                      </Tooltip>
                    </Paper>
                  </Grid>
                ),
            )}
          </Grid>

          {/* PHẦN DƯỚI: THỐNG KÊ CHI TIẾT (GIỐNG ẢNH MẪU) */}
          {/* <Grid container spacing={3} sx={{ mt: 3 }}>
          <Grid item xs={12} md={8}>
            <Paper elevation={0} sx={{ p: 3, borderRadius: '20px', border: '1px solid #E2E8F0', minHeight: 300 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                Biểu đồ hoạt động
              </Typography>
              <Box
                sx={{
                  height: 200,
                  bgcolor: '#F8FAFC',
                  borderRadius: '12px',
                  border: '2px dashed #E2E8F0',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Typography color="textSecondary">Khu vực hiển thị Chart doanh số/truy cập</Typography>
              </Box>
            </Paper>
          </Grid>
          <Grid item xs={12} md={4}>
            <Paper elevation={0} sx={{ p: 3, borderRadius: '20px', border: '1px solid #E2E8F0', minHeight: 300 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                Thông báo mới
              </Typography>
              <Stack spacing={2}>
                {[1, 2, 3].map((i) => (
                  <Box
                    key={i}
                    sx={{ p: 1.5, bgcolor: '#F8FAFC', borderRadius: '8px', borderLeft: '4px solid #3B82F6' }}
                  >
                    <Typography sx={{ fontSize: '0.8rem', fontWeight: 600 }}>Cập nhật hệ thống v2.0</Typography>
                    <Typography sx={{ fontSize: '0.7rem', color: '#64748B' }}>2 giờ trước</Typography>
                  </Box>
                ))}
              </Stack>
            </Paper>
          </Grid>
        </Grid> */}
        </Box>
      </Box>
    </Container>
  );
}

export default Dashboard;
